// Ficha Cliente 360° — Sprint S5 · Módulo A (Módulo de Ventas v2)
// Presenta: panel 8 indicadores + 3 sub-tabs (Facturación, Artículos, Pedidos).
// IMPORTE (coste) y Documento origen: preparados pero EXCLUIDOS (por decisión de usuario).
import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { fichaClienteApi, analyticsComercialesApi } from '../../services/api';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, PackageCheck,
  Clock, Truck, Sparkles, FileText, Receipt, BarChart3, Loader2,
} from 'lucide-react';

const fmtEur = (v) => {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
};
const fmtNum = (v) => v === null || v === undefined ? '—' : new Intl.NumberFormat('es-ES').format(v);
const fmtPct = (v) => v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v}%`;

const Kpi = ({ icon: Icon, label, value, sub, tone = 'default', testid }) => {
  const tones = {
    default: 'bg-white border-ink-200',
    good: 'bg-emerald-50 border-emerald-200',
    warn: 'bg-amber-50 border-amber-200',
    bad: 'bg-red-50 border-red-200',
  };
  return (
    <Card className={`p-4 border ${tones[tone] || tones.default}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-ink-500 uppercase tracking-wider">{label}</div>
          <div className="mt-2 text-2xl font-heading font-bold text-ink-900 leading-tight">{value}</div>
          {sub && <div className="mt-1 text-xs text-ink-500">{sub}</div>}
        </div>
        {Icon && <Icon className="w-5 h-5 text-ink-400 shrink-0" />}
      </div>
    </Card>
  );
};

const SubTabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
      active
        ? 'bg-brand-50 border-brand-200 text-brand-700'
        : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

// ============================================================
// Panel 8 indicadores
// ============================================================
const PanelResumen = ({ data }) => {
  if (!data) return null;
  const variacion = data.variacion_vs_ano_anterior_pct;
  const variacionTone = variacion === null ? 'default' : variacion >= 0 ? 'good' : 'bad';
  const VarIcon = variacion === null ? Minus : variacion >= 0 ? TrendingUp : TrendingDown;

  const alertaUltimo = data.alerta_ultimo_pedido;
  const ultimoTone = alertaUltimo === 'critica' ? 'bad' : alertaUltimo === 'leve' ? 'warn' : alertaUltimo === 'sin_pedidos' ? 'bad' : 'default';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ficha-panel-resumen">
      <Kpi
        icon={Receipt}
        label="Facturación YTD"
        value={fmtEur(data.facturacion_ytd)}
        sub={`Año anterior (total): ${fmtEur(data.facturacion_ano_anterior_completo)}`}
        testid="kpi-fact-ytd"
      />
      <Kpi
        icon={VarIcon}
        label="Variación vs año anterior"
        value={variacion === null ? '—' : fmtPct(variacion)}
        sub={`Mismo periodo anterior: ${fmtEur(data.facturacion_ano_anterior_mismo_periodo)}`}
        tone={variacionTone}
        testid="kpi-variacion"
      />
      <Kpi
        icon={Clock}
        label="Último pedido"
        value={data.dias_desde_ultimo_pedido === null ? 'Sin pedidos' : `Hace ${data.dias_desde_ultimo_pedido}d`}
        sub={data.ultimo_pedido_fecha || '—'}
        tone={ultimoTone}
        testid="kpi-ultimo-pedido"
      />
      <Kpi
        icon={PackageCheck}
        label="Pedidos pendientes"
        value={fmtNum(data.pedidos_pendientes_lineas)}
        sub={`${fmtEur(data.pedidos_pendientes_importe)} en curso`}
        tone={data.pedidos_pendientes_lineas > 0 ? 'warn' : 'good'}
        testid="kpi-pedidos-pendientes"
      />
      <Kpi
        icon={Truck}
        label="Plazo entrega (medio)"
        value={data.plazo_medio_primer_albaran === null ? '—' : `${data.plazo_medio_primer_albaran}d`}
        sub="Pedido → 1er albarán (últ. 5)"
        testid="kpi-plazo-entrega"
      />
      <Kpi
        icon={Clock}
        label="Plazo cierre (medio)"
        value={data.plazo_medio_cierre_completo === null ? '—' : `${data.plazo_medio_cierre_completo}d`}
        sub="Pedido → último albarán"
        testid="kpi-plazo-cierre"
      />
      <Kpi
        icon={FileText}
        label="Ofertas abiertas"
        value={fmtNum(data.ofertas_abiertas)}
        sub={`${fmtEur(data.ofertas_abiertas_importe)} potenciales`}
        tone={data.ofertas_abiertas > 0 ? 'warn' : 'default'}
        testid="kpi-ofertas-abiertas"
      />
      <Kpi
        icon={Sparkles}
        label="Tasa conversión oferta"
        value={data.tasa_conversion_oferta_pct === null ? '—' : `${data.tasa_conversion_oferta_pct}%`}
        sub="Ofertas → pedido · 12m"
        tone={
          data.tasa_conversion_oferta_pct === null
            ? 'default'
            : data.tasa_conversion_oferta_pct >= 50
              ? 'good'
              : data.tasa_conversion_oferta_pct >= 25
                ? 'warn'
                : 'bad'
        }
        testid="kpi-tasa-conversion"
      />
    </div>
  );
};

// ============================================================
// Tab: Evolución Facturación
// ============================================================
const TabFacturacion = ({ entidadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fichaClienteApi.facturacion(entidadId).then(setData).finally(() => setLoading(false));
  }, [entidadId]);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando evolución…</div>;
  if (!data) return <Card className="p-6 text-ink-500">Sin datos.</Card>;

  const hayDatos = data.serie_mensual?.some((m) => m.actual > 0 || m.anterior > 0);

  return (
    <div className="space-y-4" data-testid="tab-facturacion">
      <Card className="p-4 border-ink-200 bg-white">
        <h3 className="text-sm font-semibold text-ink-800 mb-3">Evolución mensual (€) · actual vs año anterior</h3>
        {hayDatos ? (
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={data.serie_mensual} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="mes" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtEur(v)} />
                <Legend />
                <Line type="monotone" dataKey="anterior" name="Año anterior" stroke="#9ca3af" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="actual" name="Año actual" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-ink-500 italic">Sin facturación registrada en los últimos 24 meses.</p>
        )}
      </Card>

      <Card className="p-4 border-ink-200 bg-white">
        <h3 className="text-sm font-semibold text-ink-800 mb-3">Resumen anual</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-4 font-medium">Año</th>
                <th className="py-2 pr-4 font-medium">Facturación</th>
                <th className="py-2 pr-4 font-medium">Nº facturas</th>
                <th className="py-2 pr-4 font-medium">Ticket medio</th>
                <th className="py-2 font-medium">Variación</th>
              </tr>
            </thead>
            <tbody>
              {data.tabla_anual.map((row) => (
                <tr key={row.ano} className="border-b border-ink-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-ink-800">{row.ano}</td>
                  <td className="py-2 pr-4">{fmtEur(row.facturacion)}</td>
                  <td className="py-2 pr-4">{row.n_facturas}</td>
                  <td className="py-2 pr-4">{fmtEur(row.ticket_medio)}</td>
                  <td className="py-2">
                    {row.variacion_pct === null ? (
                      <span className="text-ink-400">—</span>
                    ) : (
                      <Badge className={`border ${row.variacion_pct >= 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        {fmtPct(row.variacion_pct)}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-400 mt-3 italic">
          * Rentabilidad/margen no disponible — el campo IMPORTE (coste) llega a 0€ desde Inology. Solicitado a IT/Inology.
        </p>
      </Card>
    </div>
  );
};

// ============================================================
// Tab: Artículos
// ============================================================
const TENDENCIA_BADGE = {
  creciente: { l: 'Creciendo',  cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: TrendingUp },
  estable:   { l: 'Estable',    cls: 'bg-ink-50 border-ink-200 text-ink-700',             icon: Minus },
  declive:   { l: 'En declive', cls: 'bg-amber-50 border-amber-200 text-amber-700',       icon: TrendingDown },
  perdido:   { l: 'Perdido',    cls: 'bg-red-50 border-red-200 text-red-700',             icon: AlertTriangle },
};

const TabArticulos = ({ entidadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');

  useEffect(() => {
    setLoading(true);
    fichaClienteApi.articulos(entidadId).then(setData).finally(() => setLoading(false));
  }, [entidadId]);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando artículos…</div>;
  if (!data || !data.items.length) return <Card className="p-6 text-ink-500" data-testid="tab-articulos-empty">No hay artículos facturados para este cliente.</Card>;

  const items = filter === 'todos' ? data.items : data.items.filter((x) => x.tendencia === filter);

  return (
    <div className="space-y-4" data-testid="tab-articulos">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total artículos" value={fmtNum(data.total_articulos)} testid="art-total" />
        <Kpi label="Creciendo" value={fmtNum(data.articulos_creciendo)} tone="good" testid="art-creciendo" />
        <Kpi label="En declive" value={fmtNum(data.articulos_en_declive)} tone={data.articulos_en_declive > 0 ? 'warn' : 'default'} testid="art-declive" />
        <Kpi label="Perdidos" value={fmtNum(data.articulos_perdidos)} tone={data.articulos_perdidos > 0 ? 'bad' : 'default'} testid="art-perdidos" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {['todos', 'creciente', 'estable', 'declive', 'perdido'].map((f) => (
          <SubTabButton key={f} active={filter === f} onClick={() => setFilter(f)} testid={`art-filter-${f}`}>
            {f === 'todos' ? 'Todos' : TENDENCIA_BADGE[f].l}
          </SubTabButton>
        ))}
      </div>

      <Card className="p-0 border-ink-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-left text-ink-600 border-b border-ink-200">
                <th className="py-2 px-3 font-medium">Artículo</th>
                <th className="py-2 px-3 font-medium text-right">Uds año ant.</th>
                <th className="py-2 px-3 font-medium text-right">Uds año actual</th>
                <th className="py-2 px-3 font-medium text-right">Variación</th>
                <th className="py-2 px-3 font-medium text-right">Facturado actual</th>
                <th className="py-2 px-3 font-medium">Última compra</th>
                <th className="py-2 px-3 font-medium">Tendencia</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const badge = TENDENCIA_BADGE[a.tendencia] || TENDENCIA_BADGE.estable;
                const TIcon = badge.icon;
                return (
                  <tr key={a.articulo_codigo} className="border-b border-ink-100 last:border-0 hover:bg-ink-50/50">
                    <td className="py-2 px-3">
                      <div className="font-mono text-xs text-ink-500">{a.articulo_codigo}</div>
                      <div className="text-ink-800 truncate max-w-xs">{a.descripcion || '—'}</div>
                    </td>
                    <td className="py-2 px-3 text-right">{fmtNum(a.uds_anterior)}</td>
                    <td className="py-2 px-3 text-right font-medium">{fmtNum(a.uds_actual)}</td>
                    <td className="py-2 px-3 text-right">
                      {a.variacion_pct === null ? <span className="text-ink-400">—</span> :
                        <span className={a.variacion_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}>{fmtPct(a.variacion_pct)}</span>
                      }
                    </td>
                    <td className="py-2 px-3 text-right">{fmtEur(a.importe_actual)}</td>
                    <td className="py-2 px-3 text-ink-600">
                      {a.ultimo_albaran || '—'}
                      {a.dias_sin_compra !== null && <span className="text-ink-400 ml-1">({a.dias_sin_compra}d)</span>}
                    </td>
                    <td className="py-2 px-3">
                      <Badge className={`border ${badge.cls} inline-flex items-center gap-1`}>
                        <TIcon className="w-3 h-3" /> {badge.l}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// Tab: Familias (penetración de catálogo)
// ============================================================
const FAMILIA_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-violet-100 text-violet-700 border-violet-200',
  'bg-emerald-100 text-emerald-700 border-emerald-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-orange-100 text-orange-700 border-orange-200',
  'bg-teal-100 text-teal-700 border-teal-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
];

const TabFamilias = ({ entidadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    analyticsComercialesApi.penetracionCliente(entidadId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [entidadId]);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando familias…</div>;
  if (!data || !data.detalle?.length) return (
    <Card className="p-6 text-ink-500" data-testid="tab-familias-empty">
      No hay datos de familias para este cliente. Ejecuta "Clasificar desde documentos" en la sección Artículos ERP.
    </Card>
  );

  const { detalle, familias_nuevas, familias_perdidas } = data;
  const maxImporte = Math.max(...detalle.map((f) => f.importe_periodo), 1);

  return (
    <div className="space-y-4" data-testid="tab-familias">
      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Familias activas" value={fmtNum(detalle.length)} testid="fam-activas" />
        <Kpi
          label="Familias nuevas este año"
          value={fmtNum(familias_nuevas.length)}
          tone={familias_nuevas.length > 0 ? 'good' : 'default'}
          testid="fam-nuevas"
        />
        <Kpi
          label="Familias perdidas"
          value={fmtNum(familias_perdidas.length)}
          tone={familias_perdidas.length > 0 ? 'warn' : 'default'}
          testid="fam-perdidas"
        />
      </div>

      {/* Chips de familias activas */}
      <div className="flex flex-wrap gap-2">
        {detalle.map((f, i) => (
          <span
            key={f.familia_id}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${FAMILIA_COLORS[i % FAMILIA_COLORS.length]}`}
            data-testid={`fam-chip-${f.familia_id}`}
          >
            {f.familia_nombre}
            {f.es_nueva && (
              <span className="bg-emerald-500 text-white text-[10px] px-1 py-0.5 rounded font-bold leading-none">NUEVA</span>
            )}
          </span>
        ))}
        {familias_perdidas.length > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-red-50 text-red-600 border-red-200">
            <AlertTriangle className="w-3 h-3" /> {familias_perdidas.length} perdida{familias_perdidas.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Barras por familia */}
      <Card className="p-4 border-ink-200 bg-white">
        <div className="space-y-2">
          {detalle.map((f) => {
            const pct = Math.round((f.importe_periodo / maxImporte) * 100);
            return (
              <div key={f.familia_id} className="flex items-center gap-3">
                <div className="w-32 shrink-0 text-xs text-ink-600 truncate" title={f.familia_nombre}>
                  {f.familia_id} — {f.familia_nombre}
                </div>
                <div className="flex-1 h-3 bg-ink-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${f.es_nueva ? 'bg-emerald-400' : 'bg-brand-400'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-24 text-right text-xs font-semibold text-ink-800 shrink-0">{fmtEur(f.importe_periodo)}</div>
                {f.primera_compra && (
                  <div className="w-20 text-right text-xs text-ink-400 shrink-0">
                    {f.es_nueva ? <span className="text-emerald-600 font-medium">1ª compra</span> : f.primera_compra}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// Tab: Pedidos (estado calculado)
// ============================================================
const ESTADO_BADGE = {
  servido:    { l: 'Servido',    cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  parcial:    { l: 'Parcial',    cls: 'bg-amber-50 border-amber-200 text-amber-700' },
  pendiente:  { l: 'Pendiente',  cls: 'bg-red-50 border-red-200 text-red-700' },
};

const TabPedidos = ({ entidadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandido, setExpandido] = useState(null);
  const [filtro, setFiltro] = useState('todos');

  useEffect(() => {
    setLoading(true);
    fichaClienteApi.pedidosEstado(entidadId).then(setData).finally(() => setLoading(false));
  }, [entidadId]);

  if (loading) return <div className="flex items-center gap-2 text-ink-500 p-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando pedidos…</div>;
  if (!data || !data.items.length) return <Card className="p-6 text-ink-500" data-testid="tab-pedidos-empty">No hay pedidos registrados para este cliente.</Card>;

  const items = filtro === 'todos' ? data.items : data.items.filter((x) => x.estado === filtro);
  const r = data.resumen;
  const totalServidas = (r.servidas_una_entrega || 0) + (r.servidas_multiples_entregas || 0);

  return (
    <div className="space-y-4" data-testid="tab-pedidos">
      {/* KPIs — órdenes únicas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Órdenes" value={fmtNum(r.total_ordenes)} testid="ped-total" />
        <Kpi
          label="Servidas"
          value={fmtNum(totalServidas)}
          tone={totalServidas > 0 ? 'good' : 'default'}
          sub={totalServidas > 0 ? `${r.servidas_una_entrega} directas · ${r.servidas_multiples_entregas} multi` : undefined}
          testid="ped-servidos"
        />
        <Kpi label="Parciales"  value={fmtNum(r.parciales)}  tone={r.parciales  > 0 ? 'warn' : 'default'} testid="ped-parciales" />
        <Kpi label="Pendientes" value={fmtNum(r.pendientes)} tone={r.pendientes > 0 ? 'bad'  : 'default'} testid="ped-pendientes" />
        <Kpi
          label="Plazo entrega"
          value={data.plazo_medio_pedido_primer_albaran === null ? '—' : `${data.plazo_medio_pedido_primer_albaran}d`}
          sub={data.plazo_medio_cierre_completo === null ? '' : `Cierre: ${data.plazo_medio_cierre_completo}d`}
          testid="ped-plazos"
        />
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        {['todos', 'servido', 'parcial', 'pendiente'].map((f) => (
          <SubTabButton key={f} active={filtro === f} onClick={() => setFiltro(f)} testid={`ped-filter-${f}`}>
            {f === 'todos' ? 'Todos' : ESTADO_BADGE[f].l}
          </SubTabButton>
        ))}
      </div>

      {/* Tabla nivel 1 */}
      <Card className="p-0 border-ink-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr className="text-left text-ink-600 border-b border-ink-200">
                <th className="py-2 px-2 w-6"></th>
                <th className="py-2 px-3 font-medium">Pedido</th>
                <th className="py-2 px-3 font-medium">Fecha</th>
                <th className="py-2 px-3 font-medium text-right">Total acum.</th>
                <th className="py-2 px-3 font-medium text-right">Facturado</th>
                <th className="py-2 px-3 font-medium text-right">Pendiente</th>
                <th className="py-2 px-3 font-medium">% servido</th>
                <th className="py-2 px-3 font-medium">Estado</th>
                <th className="py-2 px-3 font-medium">Días</th>
                <th className="py-2 px-3 font-medium text-right">Entregas</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const badge = ESTADO_BADGE[p.estado] || ESTADO_BADGE.pendiente;
                const abierto = expandido === p.numero_base;
                return (
                  <React.Fragment key={p.numero_base}>
                    {/* Nivel 1 */}
                    <tr
                      className="border-b border-ink-100 hover:bg-ink-50/50 cursor-pointer"
                      onClick={() => setExpandido(abierto ? null : p.numero_base)}
                      data-testid={`ped-row-${p.numero_base}`}
                    >
                      <td className="py-2 px-2 text-ink-400 text-xs">{abierto ? '▲' : '▶'}</td>
                      <td className="py-2 px-3 font-mono text-xs text-ink-700">{p.numero_base}</td>
                      <td className="py-2 px-3 text-ink-600">{p.fecha_primera_version || '—'}</td>
                      <td className="py-2 px-3 text-right">{fmtEur(p.importe_total_acumulado)}</td>
                      <td className="py-2 px-3 text-right text-ink-600">{fmtEur(p.importe_facturado)}</td>
                      <td className="py-2 px-3 text-right">
                        {p.importe_pendiente > 0
                          ? <span className="text-red-700 font-medium">{fmtEur(p.importe_pendiente)}</span>
                          : <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-ink-100 rounded overflow-hidden">
                            <div
                              className={`h-full ${p.estado === 'servido' ? 'bg-emerald-500' : p.estado === 'parcial' ? 'bg-amber-500' : 'bg-red-400'}`}
                              style={{ width: `${p.porcentaje_servido}%` }}
                            />
                          </div>
                          <span className="text-xs text-ink-500">{p.porcentaje_servido}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={`border ${badge.cls}`}>{badge.l}</Badge>
                      </td>
                      <td className="py-2 px-3 text-ink-600">{p.dias_abierto === null ? '—' : `${p.dias_abierto}d`}</td>
                      <td className="py-2 px-3 text-right text-ink-500">{p.num_versiones}</td>
                    </tr>
                    {/* Nivel 2 — detalle de entregas */}
                    {abierto && (
                      <tr className="bg-ink-50/60">
                        <td colSpan={10} className="px-4 py-3">
                          <div className="text-xs font-semibold text-ink-600 mb-2">
                            Entregas ({p.versiones.length})
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-ink-500 border-b border-ink-200">
                                <th className="py-1 pr-3 text-left font-medium">Entrega</th>
                                <th className="py-1 pr-3 text-right font-medium">Importe</th>
                                <th className="py-1 pr-3 font-medium">Estado</th>
                                <th className="py-1 pr-3 font-medium">Factura(s)</th>
                                <th className="py-1 font-medium">Fecha factura</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.versiones.map((v) => {
                                const vb = ESTADO_BADGE[v.estado] || ESTADO_BADGE.pendiente;
                                return (
                                  <tr key={v.numero_version} className="border-b border-ink-100 last:border-0">
                                    <td className="py-1 pr-3 font-mono text-ink-700">
                                      {v.numero}
                                      {v.es_ultima && <span className="ml-1 text-[10px] text-ink-400">(última)</span>}
                                    </td>
                                    <td className="py-1 pr-3 text-right">{fmtEur(v.importe)}</td>
                                    <td className="py-1 pr-3">
                                      <Badge className={`border ${vb.cls} text-[10px] px-1.5 py-0`}>{vb.l}</Badge>
                                    </td>
                                    <td className="py-1 pr-3 font-mono text-ink-600">
                                      {v.facturas.length > 0
                                        ? v.facturas.map((f) => f.numero).join(', ')
                                        : <span className="text-ink-300">—</span>}
                                    </td>
                                    <td className="py-1 text-ink-500">
                                      {v.facturas.length > 0 ? (v.facturas[0].fecha || '—') : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// Contenedor principal — Panel + sub-tabs
// ============================================================
const VentasFicha360 = ({ entidadId }) => {
  const [sub, setSub] = useState('facturacion');
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(true);

  useEffect(() => {
    if (!entidadId) return;
    setLoadingResumen(true);
    fichaClienteApi.resumen(entidadId).then(setResumen).catch(() => setResumen(null)).finally(() => setLoadingResumen(false));
  }, [entidadId]);

  return (
    <div className="space-y-5" data-testid="ventas-ficha-360">
      {loadingResumen ? (
        <div className="flex items-center gap-2 text-ink-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando panel 360°…</div>
      ) : (
        <PanelResumen data={resumen} />
      )}

      <div className="flex gap-2 flex-wrap border-b border-ink-200 pb-3">
        <SubTabButton active={sub === 'facturacion'} onClick={() => setSub('facturacion')} testid="sub-facturacion">
          <BarChart3 className="w-4 h-4" /> Evolución facturación
        </SubTabButton>
        <SubTabButton active={sub === 'articulos'} onClick={() => setSub('articulos')} testid="sub-articulos">
          <PackageCheck className="w-4 h-4" /> Artículos
        </SubTabButton>
        <SubTabButton active={sub === 'familias'} onClick={() => setSub('familias')} testid="sub-familias">
          <BarChart3 className="w-4 h-4" /> Familias
        </SubTabButton>
        <SubTabButton active={sub === 'pedidos'} onClick={() => setSub('pedidos')} testid="sub-pedidos">
          <Truck className="w-4 h-4" /> Pedidos
        </SubTabButton>
      </div>

      {sub === 'facturacion' && <TabFacturacion entidadId={entidadId} />}
      {sub === 'articulos' && <TabArticulos entidadId={entidadId} />}
      {sub === 'familias' && <TabFamilias entidadId={entidadId} />}
      {sub === 'pedidos' && <TabPedidos entidadId={entidadId} />}
    </div>
  );
};

export default VentasFicha360;
